import { useEffect, useRef } from 'react';
import { Button, Image } from '@cowbell/ui';

import { PubSub } from '../../utils/eventUtils';
import { Modal } from '../../components/modals/v2/helpers/v2.modal.helpers';
import { QualysFormConfig } from './QualysForm';
import { SecurityStudioTokenFormConfig } from './SecurityStudioTokenForm';
import { WizerTokenFormConfig } from './WizerTokenForm';
import { PrivacyConsentConfig } from '../../components/modals/PrivacyConsent';
import { connectorModalTitles } from '../../components/modals/v2/connectors/titles.constants';
import { CONNECTOR_TYPES } from './connectors.constants';
import { iconMappings } from '../../console/_global/navigation/IconsMappings';
import { AWSConnectorConfig } from '../../components/modals/AWSConnector';
import { GenericConnectorConfig } from '../../components/modals/v2/GenericConnector';
import { getOAuthMetadata } from './ConnectorService';
import ConnectorCard from './ConnectorCard';
import { catalogStatusLabel, type Connector } from './connectors.view';
import styles from './Connectors.module.css';

const isNewConnectorType = (connectorType: string) => {
  return (
    [
      CONNECTOR_TYPES.CLOUDFLARE,
      CONNECTOR_TYPES.GSC,
      CONNECTOR_TYPES.GOOGLEWORKSPACE,
      CONNECTOR_TYPES.SOPHOS,
      CONNECTOR_TYPES.LANGFUSE,
      CONNECTOR_TYPES.GEMINI,
      CONNECTOR_TYPES.CLAUDE,
    ] as string[]
  ).includes(connectorType);
};

const modalIcon = (src: string) => (
  <Image src={src} alt="" className={styles.modalIcon} />
);

interface AvailableTileProps {
  readonly data: Connector;
  readonly cbid: string | null;
  readonly tag?: string;
}

const AvailableTile = ({ data, cbid, tag }: AvailableTileProps) => {
  const { connectorType, name, description, connectorListStatus } = data;
  const connectorMetadata = useRef<any>(null);
  const isAvailable = connectorListStatus === 'AVAILABLE';

  useEffect(() => {
    if (isAvailable) {
      const sub = PubSub.subscribe('privacyConsent', (eventData: any) => {
        if (eventData.connectorType === connectorType) {
          handleClick();
        }
      });
      return () => {
        sub.remove();
      };
    }
    // eslint-disable-next-line
  }, []);

  // call getOAuthMetadata in useEffect and store the response in state connectorMetadata
  useEffect(() => {
    if (
      connectorType === CONNECTOR_TYPES.MICROSOFT ||
      connectorType === CONNECTOR_TYPES.SAFEGUARD ||
      connectorType === CONNECTOR_TYPES.SECUREWORKS
    ) {
      getOAuthMetadata({ connectorType }).then((response: any) => {
        connectorMetadata.current = response.data;
      });
    }
  }, [connectorType]);

  const handleClick = () => {
    if (
      connectorType === CONNECTOR_TYPES.QUALYSPC ||
      connectorType === CONNECTOR_TYPES.QUALYSVM ||
      connectorType === CONNECTOR_TYPES.QUALYSCS
    ) {
      Modal.show(QualysFormConfig, {
        data: { cbid, connectorType },
        config: { icon: modalIcon('images/Qualys.svg') },
      });
    } else if (connectorType === CONNECTOR_TYPES.SECURITYSTUDIO) {
      Modal.show(SecurityStudioTokenFormConfig, {
        data: { cbid },
        config: { icon: modalIcon('images/securityStudio_dark.svg') },
      });
    } else if (connectorType === CONNECTOR_TYPES.WIZER) {
      Modal.show(WizerTokenFormConfig, {
        data: { cbid },
        config: { icon: modalIcon('images/Wizer_DarkLogo.svg') },
      });
    } else if (connectorType === CONNECTOR_TYPES.AWS) {
      Modal.show(AWSConnectorConfig);
    } else if (isNewConnectorType(connectorType)) {
      Modal.show(GenericConnectorConfig, {
        data: { cbid, connectorType },
        config: {
          title: connectorModalTitles[connectorType],
          icon: iconMappings[connectorType].iconImport,
        },
      });
    } else {
      // OAuth connectors navigate away rather than showing a modal, so nothing
      // replaces the consent dialog in the shared host the way the branches
      // above do. Dismiss it here, before the metadata read below, which throws
      // if the OAuth lookup hasn't resolved yet.
      Modal.hide();

      let prompt = connectorType === 'GOOGLE' ? 'consent' : 'select_account';
      const accessType =
        connectorType === 'GOOGLE' ? '&access_type=offline' : '';

      const {
        clientId,
        responseType,
        redirectUri,
        scope,
        authPortalUri,
        model = '',
        state,
      } = connectorMetadata.current;

      let modelParam = '';

      if (connectorType === CONNECTOR_TYPES.SAFEGUARD) {
        modelParam = `&model=${model}`;
        prompt = '';
      }

      window.location.href = `${authPortalUri}?client_id=${clientId}&response_type=${responseType}&redirect_uri=${encodeURI(
        redirectUri
      )}&response_mode=query&scope=${scope.replace(
        /,/g,
        '%20'
      )}&state=${state}.${cbid}&prompt=${prompt}${accessType}${modelParam}`;
    }
  };

  const showConsent = () => {
    Modal.show(PrivacyConsentConfig, { data: { data } });
  };

  return (
    <ConnectorCard
      name={name}
      description={description}
      connectorType={connectorType}
      logoUrl={data.logoUrl}
      tag={tag}
      status={
        isAvailable
          ? undefined
          : { kind: 'neutral', label: catalogStatusLabel(connectorListStatus) }
      }
      onSelect={isAvailable ? showConsent : undefined}
      actions={
        isAvailable && (
          <Button variant="light" size="compact-sm" onClick={showConsent}>
            Connect
          </Button>
        )
      }
    />
  );
};

export default AvailableTile;
